import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, Fieldset, Legend, Text, Heading } from '../components/ui'
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/16/solid'
import { BRAND_COLOR } from '../constants'
import { useTheme } from '../hooks'
import { useAuthContext } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

const ResetPassword: React.FC = () => {
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { updatePassword, loading, error } = useAuthContext()
  const [resetError, setResetError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [debugInfo, setDebugInfo] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(true)

  useEffect(() => {
    const processResetPassword = async () => {
      try {
        // Debug: Mostrar todos os parâmetros da URL
        const allParams = Object.fromEntries(searchParams.entries())
        console.log('🔍 Todos os parâmetros da URL:', allParams)
        setDebugInfo(`Parâmetros encontrados: ${JSON.stringify(allParams, null, 2)}`)
        
        // Tentar diferentes formas de capturar o token
        const accessToken = searchParams.get('access_token')
        const refreshToken = searchParams.get('refresh_token')
        const tokenParam = searchParams.get('token')
        const typeParam = searchParams.get('type')
        
        console.log('🔍 Tokens encontrados:', { 
          accessToken, 
          refreshToken, 
          tokenParam, 
          typeParam 
        })
        
        // Se não há tokens na URL, verificar se o Supabase já processou o reset
        if (!accessToken && !refreshToken && !tokenParam) {
          console.log('🔍 Nenhum token na URL, verificando sessão do Supabase...')
          
          // Verificar se há uma sessão ativa do Supabase
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()
          
          if (sessionError) {
            console.error('❌ Erro ao obter sessão:', sessionError)
            setSubmitError('Erro ao verificar sessão. Tente novamente.')
            setIsProcessing(false)
            return
          }
          
          if (session) {
            console.log('✅ Sessão encontrada, usuário já autenticado para reset')
            setToken('session-active') // Marcador especial para sessão ativa
            setIsProcessing(false)
            return
          } else {
            console.log('❌ Nenhuma sessão encontrada')
            setSubmitError('Token de redefinição não encontrado ou expirado. Solicite um novo reset de senha.')
            setIsProcessing(false)
            return
          }
        }
        
        // O Supabase pode enviar o token de diferentes formas
        if (accessToken) {
          console.log('✅ Usando access_token')
          setToken(accessToken)
        } else if (tokenParam) {
          console.log('✅ Usando token parameter')
          setToken(tokenParam)
        } else if (typeParam === 'recovery' && refreshToken) {
          console.log('✅ Usando refresh_token para recovery')
          setToken(refreshToken)
        } else {
          console.log('❌ Nenhum token válido encontrado')
          setSubmitError('Token de redefinição não encontrado. Verifique o link do email.')
          
          // Debug adicional
          console.log('🔍 URL completa:', window.location.href)
          console.log('🔍 Hash da URL:', window.location.hash)
          console.log('🔍 Search da URL:', window.location.search)
        }
        
        setIsProcessing(false)
      } catch (error) {
        console.error('❌ Erro ao processar reset de senha:', error)
        setSubmitError('Erro inesperado ao processar reset de senha.')
        setIsProcessing(false)
      }
    }

    processResetPassword()
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!token) {
      setSubmitError('Token de redefinição não encontrado.')
      return
    }

    // Validações
    if (formData.password !== formData.confirmPassword) {
      setSubmitError('As senhas não coincidem')
      return
    }

    if (formData.password.length < 6) {
      setSubmitError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    try {
      console.log('🔍 Iniciando redefinição de senha...')
      console.log('🔍 Token usado:', token === 'session-active' ? 'Sessão ativa' : 'Token presente')
      
      let result
      
      if (token === 'session-active') {
        // Se temos uma sessão ativa, usar updatePassword diretamente
        result = await updatePassword(formData.password)
      } else {
        // Se temos um token, usar updatePassword normalmente
        result = await updatePassword(formData.password)
      }
      
      console.log('Resultado da redefinição:', result)
      
      if (result.success) {
        console.log('✅ Senha redefinida com sucesso!')
        setIsSuccess(true)
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        console.error('❌ Erro na redefinição:', result.error)
        setResetError(result.error || 'Erro ao redefinir senha')
        setSubmitError(result.error || 'Erro ao redefinir senha')
      }
    } catch (error) {
      console.error('❌ Erro inesperado na redefinição:', error)
      setSubmitError('Erro inesperado. Tente novamente.')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const handleRequestNewReset = () => {
    navigate('/forgot-password')
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img 
              src="/logo-pasi.svg" 
              alt="PASI Logo" 
              className="w-12 h-12 rounded-lg object-contain"
            />
          </div>
          <Heading as="h2" size="2xl" className="mt-6 text-center">
            Processando...
          </Heading>
          <Text size="sm" color="muted" className="mt-2 text-center">
            Verificando token de redefinição
          </Text>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <Text size="sm" color="muted" className="mt-4">
                Aguarde enquanto verificamos seu link...
              </Text>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img 
              src="/logo-pasi.svg" 
              alt="PASI Logo" 
              className="w-12 h-12 rounded-lg object-contain"
            />
          </div>
          <Heading as="h2" size="2xl" className="mt-6 text-center">
            Senha redefinida!
          </Heading>
          <Text size="sm" color="muted" className="mt-2 text-center">
            Sua senha foi alterada com sucesso
          </Text>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <div className="mt-4">
                <Text size="lg" className="text-green-600 dark:text-green-400 font-semibold">
                  Senha alterada com sucesso!
                </Text>
                <Text size="sm" color="muted" className="mt-2">
                  Você será redirecionado para a página de login em alguns segundos.
                </Text>
              </div>

              <div className="mt-6">
                <Button
                  onClick={handleBackToLogin}
                  className="w-full text-white"
                  size="lg"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  Ir para o login
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: BRAND_COLOR }}
          >
            <span className="text-white text-xl font-bold">P</span>
          </div>
        </div>
        <Heading as="h2" size="2xl" className="mt-6 text-center">
          Redefinir senha
        </Heading>
        <Text size="sm" color="muted" className="mt-2 text-center">
          Digite sua nova senha
        </Text>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {(resetError || submitError) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>Erro:</strong> {submitError || resetError}
              
              {submitError && submitError.includes('não encontrado') && (
                <div className="mt-2">
                  <button
                    onClick={handleRequestNewReset}
                    className="text-sm underline hover:no-underline"
                  >
                    Solicitar novo reset de senha
                  </button>
                </div>
              )}
              
              {/* Debug info */}
              {debugInfo && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm font-medium">
                    Informações de debug
                  </summary>
                  <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto">
                    {debugInfo}
                  </pre>
                </details>
              )}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Fieldset>
              <Legend>Nova senha</Legend>
              <div className="mt-2 relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Digite sua nova senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </Fieldset>

            <Fieldset>
              <Legend>Confirmar nova senha</Legend>
              <div className="mt-2 relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirme sua nova senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </Fieldset>

            <div>
              <Button 
                type="submit" 
                className="w-full text-white" 
                size="lg"
                style={{ backgroundColor: BRAND_COLOR }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = '#006d75'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = BRAND_COLOR
                }}
                disabled={loading || !token}
              >
                {loading ? 'Redefinindo senha...' : 'Redefinir senha'}
              </Button>
            </div>

            <div className="text-center">
              <Text size="sm" color="muted">
                Lembrou sua senha?{' '}
                <button 
                  type="button"
                  onClick={handleBackToLogin}
                  className="font-medium"
                  style={{ color: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = BRAND_COLOR
                  }}
                  disabled={loading}
                >
                  Faça login
                </button>
              </Text>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
